import { useState } from "react";
import { ChevronDown, ChevronRight, Search } from "lucide-react";

import { ApiError } from "@/api/client";
import type { SrpEntry } from "@/api/types";
import { DryRunBanner } from "@/components/DryRunBanner";
import { FrenoDeMano } from "@/components/FrenoDeMano";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useMyProfile } from "@/hooks/useMe";
import {
  useSetAutoExecute,
  useSrpFacets,
  useSrps,
  type SrpFacetKey,
  type SrpFacets,
  type SrpFilters,
} from "@/hooks/useSrps";
import { cn, relativeAge } from "@/lib/utils";

const CLASSIFICATION: Record<string, { label: string; className: string }> = {
  "safe-idempotent": {
    label: "seguro",
    className: "bg-green-600 text-white hover:bg-green-600",
  },
  "manual-approval": {
    label: "aprobación manual",
    className: "bg-amber-500 text-white hover:bg-amber-500",
  },
  destructive: {
    label: "destructivo",
    className: "bg-red-600 text-white hover:bg-red-600",
  },
};

// Estado de ejecución por SRP, derivado de `auto_execute`. `auto` solo es
// posible en safe-idempotent ya probado; lo enciende un super_user por UI.
const EXEC_MODE = {
  auto: {
    label: "auto",
    title: "Autoejecución sin aprobación humana (solo safe-idempotent probado).",
    className: "bg-blue-600 text-white hover:bg-blue-600",
  },
  manual: {
    label: "manual",
    title: "Requiere aprobación humana antes de ejecutar (AP-12).",
    className:
      "bg-slate-200 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200",
  },
} as const;

// ── Riel de facetas ────────────────────────────────────────────────────────────
//
// Copia deliberada del riel del inventario de credenciales en vez de un
// componente compartido: aquella pantalla es superficie de otro trabajo en
// curso y extraer de ella obligaria a tocarla. Si algun dia hay una tercera,
// entonces si conviene unificar las tres de una vez.

const FACET_TITULOS: Record<SrpFacetKey, string> = {
  equipo: "Equipo",
  client: "Cliente",
  modo: "Ejecución",
  seguridad: "Seguridad",
  prueba: "Prueba",
};

const MODO_LABEL: Record<string, string> = {
  automatico: "Automática",
  aprobacion: "Pide aprobación",
};

const PRUEBA_LABEL: Record<string, string> = {
  sin_probar: "Sin probar",
  carga_y_resuelve: "Carga y resuelve",
  probado_vivo: "Probado en vivo",
};

const PRUEBA_PUNTO: Record<string, string> = {
  sin_probar: "bg-muted-foreground/40",
  carga_y_resuelve: "bg-amber-500",
  probado_vivo: "bg-green-500",
};

// Lo que necesita atención primero, igual que el estado en credenciales. Las
// demás facetas van por cantidad, que es como las devuelve el servidor.
const ORDEN_PRUEBA = ["sin_probar", "carga_y_resuelve", "probado_vivo"];

function etiquetaFaceta(k: SrpFacetKey, v: string): string {
  if (k === "modo") return MODO_LABEL[v] ?? v;
  if (k === "prueba") return PRUEBA_LABEL[v] ?? v;
  if (k === "seguridad") return CLASSIFICATION[v]?.label ?? v;
  return v;
}

function FacetRail({
  facets,
  filtros,
  onToggle,
  onClear,
}: {
  facets?: SrpFacets;
  filtros: SrpFilters;
  onToggle: (k: SrpFacetKey, v: string) => void;
  onClear: (k: SrpFacetKey) => void;
}) {
  const [buscaEquipo, setBuscaEquipo] = useState("");

  if (!facets) {
    return <div className="text-xs text-muted-foreground">Cargando filtros…</div>;
  }

  return (
    <div className="space-y-5">
      {(Object.keys(FACET_TITULOS) as SrpFacetKey[]).map((k) => {
        let valores = [...(facets[k] ?? [])];
        if (k === "prueba") {
          valores.sort((a, b) => ORDEN_PRUEBA.indexOf(a.value) - ORDEN_PRUEBA.indexOf(b.value));
        }
        // Con 49 SRP repartidos en cinco servidores la lista de equipos ya no
        // cabe de un vistazo; las demas facetas tienen dos o tres valores.
        const buscable = k === "equipo" && valores.length > 8;
        if (buscable && buscaEquipo.trim()) {
          const t = buscaEquipo.trim().toLowerCase();
          valores = valores.filter((v) => v.value.toLowerCase().includes(t));
        }
        if (!valores.length && !(filtros[k] ?? []).length) return null;
        const activos = filtros[k] ?? [];

        return (
          <div key={k} className="space-y-0.5">
            <div className="flex items-center justify-between px-1 pb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {FACET_TITULOS[k]}
              </span>
              {activos.length > 0 && (
                <button
                  type="button"
                  onClick={() => onClear(k)}
                  className="text-[10px] text-primary hover:underline"
                >
                  quitar
                </button>
              )}
            </div>
            {buscable && (
              <Input
                value={buscaEquipo}
                onChange={(e) => setBuscaEquipo(e.target.value)}
                placeholder="Filtrar equipo…"
                className="h-7 text-xs mb-1"
                aria-label="Filtrar equipo"
              />
            )}
            <div className={buscable ? "max-h-52 overflow-y-auto" : undefined}>
              {valores.map((v) => {
                const activo = activos.includes(v.value);
                return (
                  <button
                    key={v.value}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => onToggle(k, v.value)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[13px] hover:bg-accent",
                      activo && "bg-accent font-semibold",
                    )}
                  >
                    {k === "prueba" && (
                      <span
                        className={cn("size-1.5 rounded-full shrink-0", PRUEBA_PUNTO[v.value])}
                      />
                    )}
                    <span className="flex-1 truncate" title={v.value}>
                      {etiquetaFaceta(k, v.value)}
                    </span>
                    <span className="text-xs tabular-nums text-muted-foreground">{v.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Página principal ───────────────────────────────────────────────────────────

const FACET_VACIO: Record<SrpFacetKey, string[]> = {
  equipo: [],
  client: [],
  modo: [],
  seguridad: [],
  prueba: [],
};

export function SrpCatalogPage() {
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState<Record<SrpFacetKey, string[]>>(FACET_VACIO);

  // El filtrado lo hace el servidor: es el que sabe a que equipos toca cada
  // SRP (`target_hosts`) y en que estado de prueba esta, que no viajan en la
  // fila. Filtrar en el navegador obligaria a duplicar esa logica aqui.
  const filtros: SrpFilters = { q: query, ...sel };
  const { data, isPending, error } = useSrps(filtros);
  const facetsQuery = useSrpFacets(filtros);

  const { user } = useAuth();
  const profile = useMyProfile();
  const isSuper =
    (profile.data?.global_roles ?? []).includes("super_user") || user?.role === "admin";

  const setAuto = useSetAutoExecute();
  // SRP cuyo encendido (ejecución autónoma) hay que confirmar en el modal.
  const [confirm, setConfirm] = useState<SrpEntry | null>(null);
  // protocol_id de la fila expandida (una a la vez, como el historial de runs).
  const [expanded, setExpanded] = useState<string | null>(null);

  const hayFiltro =
    query.trim() !== "" || (Object.keys(sel) as SrpFacetKey[]).some((k) => sel[k].length > 0);

  function toggle(k: SrpFacetKey, v: string) {
    setSel((s) => ({
      ...s,
      [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v],
    }));
  }

  const errorMessage = error
    ? error instanceof ApiError
      ? error.message
      : "Error al cargar el catálogo de SRPs."
    : null;

  const toggleError =
    setAuto.error instanceof ApiError ? setAuto.error.message : setAuto.error ? "Error" : null;

  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Catálogo de SRPs</h1>
        <p className="text-sm text-muted-foreground">
          Protocolos de remediación que AROC tiene cargados
          {data ? ` · ${data.total} ${hayFiltro ? "coinciden" : "disponibles"}` : ""}.
        </p>
      </div>

      <DryRunBanner />

      <FrenoDeMano esSuperUser={isSuper} />

      {(errorMessage || toggleError) && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {errorMessage ?? toggleError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-6 items-start">
        <aside className="md:sticky md:top-4">
          <FacetRail
            facets={facetsQuery.data}
            filtros={filtros}
            onToggle={toggle}
            onClear={(k) => setSel((s) => ({ ...s, [k]: [] }))}
          />
        </aside>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por ID, nombre o disparador…"
                className="pl-9"
                aria-label="Buscar SRP"
              />
            </div>
            {hayFiltro && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQuery("");
                  setSel(FACET_VACIO);
                }}
              >
                Limpiar filtros
              </Button>
            )}
          </div>

          {isPending && !data ? (
            <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
              Cargando…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
              {hayFiltro ? "Ningún SRP cumple esos filtros." : "No hay SRPs cargados todavía."}
            </div>
          ) : (
            <div className="divide-y overflow-hidden rounded-md border bg-card">
              {items.map((srp) => (
                <SrpRow
                  key={srp.protocol_id}
                  srp={srp}
                  isSuper={isSuper}
                  busy={setAuto.isPending}
                  expanded={expanded === srp.protocol_id}
                  onToggle={() =>
                    setExpanded((cur) => (cur === srp.protocol_id ? null : srp.protocol_id))
                  }
                  onEnable={() => setConfirm(srp)}
                  onDisable={() =>
                    setAuto.mutate({ protocolId: srp.protocol_id, enabled: false })
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {confirm && (
        <ConfirmAutoModal
          srp={confirm}
          busy={setAuto.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() =>
            setAuto.mutate(
              { protocolId: confirm.protocol_id, enabled: true },
              { onSuccess: () => setConfirm(null) },
            )
          }
        />
      )}
    </div>
  );
}

function SrpRow({
  srp,
  isSuper,
  busy,
  expanded,
  onToggle,
  onEnable,
  onDisable,
}: {
  srp: SrpEntry;
  isSuper: boolean;
  busy: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEnable: () => void;
  onDisable: () => void;
}) {
  const cls = CLASSIFICATION[srp.classification] ?? {
    label: srp.classification || "—",
    className: "bg-muted text-muted-foreground",
  };
  const mode = srp.auto_execute ? EXEC_MODE.auto : EXEC_MODE.manual;
  const canBeAuto = srp.classification === "safe-idempotent";
  // Solo super_user puede encender; exige safe-idempotent + probado (lo valida
  // también el backend). El botón se deshabilita con el motivo en el title.
  const enableDisabled = busy || !srp.tested;
  const enableTitle = !srp.tested
    ? "Pruébalo primero: necesita ≥1 dry-run exitoso."
    : "Activar ejecución autónoma (sin aprobación humana).";

  // Que equipos toca. Sin esto el riel filtra por algo que la fila no muestra,
  // y el operador tiene que confiar en el filtro en vez de verlo.
  const equipos = srp.target_hosts ?? [];
  const equiposTexto =
    equipos.length === 0
      ? "solo pasos locales"
      : equipos.length <= 2
        ? equipos.join(", ")
        : `${equipos.slice(0, 2).join(", ")} +${equipos.length - 2}`;

  const extraTriggers = srp.triggers.length - 1;
  const triggerSummary =
    srp.triggers.length > 0
      ? `atiende «${srp.triggers[0]}»${extraTriggers > 0 ? ` +${extraTriggers}` : ""}`
      : null;

  return (
    <div data-srp-row={srp.protocol_id} className={cn(expanded && "bg-muted/20")}>
      <div className="flex items-center gap-3 p-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {expanded ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          )}
          <span className="min-w-0">
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-mono text-xs text-muted-foreground">{srp.protocol_id}</span>
              <span className="truncate font-medium leading-tight">{srp.name}</span>
            </span>
            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
              <span>{srp.target_executor || "—"}</span>
              <span aria-hidden>·</span>
              <span title={equipos.length ? equipos.join(", ") : "No sale de la maquina de AROC"}>
                {equiposTexto}
              </span>
              <span aria-hidden>·</span>
              <span>
                {srp.steps_count} paso{srp.steps_count === 1 ? "" : "s"}
              </span>
              <span aria-hidden>·</span>
              <span>v{srp.version}</span>
              <span aria-hidden>·</span>
              <span
                className={srp.tested ? "text-green-600" : "text-amber-600"}
                title={
                  srp.tested
                    ? "Dry-run exitoso: el SRP cargó y resolvió hosts. El dry-run NO ejecuta los comandos reales — verifica la seguridad en vivo antes de activar."
                    : "Aún sin un dry-run exitoso."
                }
              >
                {srp.tested ? "✓ dry-run OK" : "sin probar"}
              </span>
              {triggerSummary && (
                <>
                  <span aria-hidden>·</span>
                  <span className="truncate">{triggerSummary}</span>
                </>
              )}
            </span>
          </span>
        </button>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          <Badge className={cn("shrink-0", mode.className)} title={mode.title}>
            {mode.label}
          </Badge>
          <Badge className={cn("shrink-0", cls.className)}>{cls.label}</Badge>
          {/* Control de ejecución autónoma (solo super_user, solo safe-idempotent). */}
          {isSuper &&
            canBeAuto &&
            (srp.auto_execute ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={onDisable}>
                Desactivar auto
              </Button>
            ) : (
              <Button
                size="sm"
                variant="default"
                disabled={enableDisabled}
                title={enableTitle}
                onClick={onEnable}
              >
                Activar auto
              </Button>
            ))}
        </div>
      </div>

      {expanded && (
        <div className="space-y-3 border-t px-3 pb-3 pt-3 text-sm">
          {srp.description ? (
            <p className="text-muted-foreground">{srp.description}</p>
          ) : (
            <p className="italic text-muted-foreground">Sin descripción.</p>
          )}

          {srp.triggers.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Atiende
              </div>
              <div className="flex flex-wrap gap-1.5">
                {srp.triggers.map((t, i) => (
                  <span key={i} className="rounded bg-muted px-2 py-0.5 text-xs text-foreground">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>
              Cliente: <span className="font-medium text-foreground">{srp.client || "—"}</span>
            </span>
            {srp.schedule_blocked && (
              <span>
                Ventana bloqueada:{" "}
                <span className="font-medium text-foreground">{srp.schedule_blocked}</span>
              </span>
            )}
            <span>Sincronizado {relativeAge(srp.synced_at)}</span>
            {srp.auto_execute && srp.auto_execute_updated_by && (
              <span>
                Auto activado por{" "}
                <span className="font-medium text-foreground">{srp.auto_execute_updated_by}</span>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Modal de confirmación al ENCENDER la ejecución autónoma (acción sensible:
// el SRP correrá sin aprobación humana).
function ConfirmAutoModal({
  srp,
  busy,
  onCancel,
  onConfirm,
}: {
  srp: SrpEntry;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md space-y-4 rounded-lg border bg-card p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-lg font-semibold">Activar ejecución autónoma</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-mono text-xs">{srp.protocol_id}</span> · {srp.name}
          </p>
        </div>
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
          ⚠️ A partir de ahora este SRP correrá <b>sin aprobación humana</b> cuando se dispare.
          Solo se permite porque es <b>safe-idempotent</b> y tuvo un <b>dry-run exitoso</b> (cargó
          y resolvió). El dry-run <b>no ejecuta los comandos reales</b>: confirma que la acción en
          vivo es segura antes de activar. Podrás desactivarlo en cualquier momento.
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? "Activando…" : "Activar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
